import { z } from 'zod';
export const knowledgeLibraryCapability = 'knowledge.library.v1';
export const knowledgeAcknowledgementsCapability = 'knowledge.acknowledgements.v1';
export const knowledgeDocumentTypeSchema = z.enum([
    'sop', 'policy', 'safety', 'reference', 'faq', 'guide',
]);
export const knowledgeDocumentStatusSchema = z.enum(['draft', 'published', 'archived']);
export const knowledgeVisibilityModeSchema = z.enum(['all', 'acl']);
export const knowledgeCommandIdSchema = z.string().uuid();
export const knowledgeReasonSchema = z.string().trim().min(10).max(500);
export const knowledgeSlugSchema = z.string().trim().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/).max(120);
export const knowledgeSignedPolicyConfigurationSchema = z.strictObject({
    enabled: z.literal(true),
    pageCount: z.number().int().min(1).max(250),
    initialsRequired: z.literal(true),
    typedNameRequired: z.literal(true),
    drawnSignatureRequired: z.literal(true),
    consentText: z.string().trim().min(1).max(4_000),
});
export const knowledgeVersionContentSchema = z.strictObject({
    title: z.string().trim().min(1).max(200),
    summary: z.string().trim().max(1_000).nullable(),
    documentType: knowledgeDocumentTypeSchema,
    bodyMarkdown: z.string().trim().min(1).max(500_000),
    requiresAcknowledgement: z.boolean(),
    reviewCycleDays: z.number().int().min(1).max(3_650).nullable(),
    signedPolicy: knowledgeSignedPolicyConfigurationSchema.nullable().optional(),
}).superRefine((content, context) => {
    if (content.signedPolicy != null && !content.requiresAcknowledgement) {
        context.addIssue({
            code: 'custom',
            path: ['signedPolicy'],
            message: 'A signed policy must require exact-version acknowledgement.',
        });
    }
});
export const knowledgeDocumentVersionSchema = z.strictObject({
    id: z.string().uuid(), tenantId: z.string().uuid(), documentId: z.string().uuid(),
    versionNumber: z.number().int().positive(), sourceVersionId: z.string().uuid().nullable(),
    content: knowledgeVersionContentSchema,
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: knowledgeReasonSchema,
    createdByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const knowledgeAcknowledgementStateSchema = z.strictObject({
    required: z.boolean(), acknowledged: z.boolean(), pending: z.boolean(),
    versionId: z.string().uuid().nullable(), acknowledgedAt: z.string().datetime({ offset: true }).nullable(),
});
export const knowledgeDocumentSchema = z.strictObject({
    id: z.string().uuid(), tenantId: z.string().uuid(), slug: knowledgeSlugSchema,
    collectionId: z.string().uuid().nullable(), visibilityMode: knowledgeVisibilityModeSchema,
    status: knowledgeDocumentStatusSchema,
    publishedVersionId: z.string().uuid().nullable(), latestVersionId: z.string().uuid(),
    revision: z.number().int().positive(),
    publishedAt: z.string().datetime({ offset: true }).nullable(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }), updatedAt: z.string().datetime({ offset: true }),
    publishedVersion: knowledgeDocumentVersionSchema.nullable(),
    latestVersion: knowledgeDocumentVersionSchema,
    acknowledgement: knowledgeAcknowledgementStateSchema,
});
export const knowledgeAcknowledgementSchema = z.strictObject({
    id: z.string().uuid(), tenantId: z.string().uuid(), documentId: z.string().uuid(),
    versionId: z.string().uuid(), membershipId: z.string().uuid(),
    acknowledgementText: z.string().trim().min(1).max(500),
    acknowledgedAt: z.string().datetime({ offset: true }),
});
export const knowledgeDocumentListQuerySchema = z.strictObject({
    search: z.string().trim().max(160).default(''),
    documentType: knowledgeDocumentTypeSchema.optional(),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(250).default(100),
    offset: z.number().int().nonnegative().default(0),
});
export const knowledgeCreateDocumentSchema = z.strictObject({
    commandId: knowledgeCommandIdSchema, slug: knowledgeSlugSchema,
    content: knowledgeVersionContentSchema, reason: knowledgeReasonSchema,
});
export const knowledgeAppendVersionSchema = z.strictObject({
    commandId: knowledgeCommandIdSchema, expectedRevision: z.number().int().positive(),
    content: knowledgeVersionContentSchema, reason: knowledgeReasonSchema,
});
export const knowledgePublishDocumentSchema = z.strictObject({
    commandId: knowledgeCommandIdSchema, expectedRevision: z.number().int().positive(),
    versionId: z.string().uuid(), reason: knowledgeReasonSchema,
});
export const knowledgeArchiveDocumentSchema = z.strictObject({
    commandId: knowledgeCommandIdSchema, expectedRevision: z.number().int().positive(),
    reason: knowledgeReasonSchema,
});
export const knowledgeRollbackDocumentSchema = z.strictObject({
    commandId: knowledgeCommandIdSchema, expectedRevision: z.number().int().positive(),
    sourceVersionId: z.string().uuid(), reason: knowledgeReasonSchema,
});
export const knowledgeAcknowledgeDocumentSchema = z.strictObject({
    commandId: knowledgeCommandIdSchema, expectedVersionId: z.string().uuid(),
    acknowledgementText: z.string().trim().min(1).max(500),
});
export const knowledgeCommandMetadataSchema = z.strictObject({
    commandId: z.string().uuid(), idempotent: z.boolean(),
});
export const knowledgeDocumentResponseSchema = z.strictObject({
    data: knowledgeDocumentSchema, command: knowledgeCommandMetadataSchema.optional(),
});
export const knowledgeDocumentsResponseSchema = z.strictObject({
    data: z.array(knowledgeDocumentSchema),
    page: z.strictObject({
        limit: z.number().int().positive(), offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(), total: z.number().int().nonnegative(),
    }),
});
export const knowledgeVersionsResponseSchema = z.strictObject({
    data: z.array(knowledgeDocumentVersionSchema),
});
export const knowledgeAcknowledgementResponseSchema = z.strictObject({
    data: knowledgeAcknowledgementSchema, command: knowledgeCommandMetadataSchema,
});
export const knowledgeAcknowledgementReportSchema = z.strictObject({
    documentId: z.string().uuid(), versionId: z.string().uuid(),
    requiredMembershipCount: z.number().int().nonnegative(),
    acknowledgedMembershipCount: z.number().int().nonnegative(),
    pendingMembershipCount: z.number().int().nonnegative(),
    rows: z.array(z.strictObject({
        membershipId: z.string().uuid(), displayName: z.string().min(1).max(200),
        acknowledged: z.boolean(), acknowledgedAt: z.string().datetime({ offset: true }).nullable(),
    })),
});
export const knowledgeAcknowledgementReportResponseSchema = z.strictObject({
    data: knowledgeAcknowledgementReportSchema,
});
//# sourceMappingURL=knowledge-library.contract.js.map